/* @img-migrated */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Activity,
  Circle,
  RefreshCcw,
  Search,
  Shield,
  Users,
  Clock,
  UserCheck,
  UserX,
  Eye,
} from 'lucide-react';
import SortableTable from '../../components/SortableTable';

import Img from '../../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '—';
  }
};

const TYPE_COLOR = {
  admin: 'bg-purple-100 text-purple-700',
  leadership: 'bg-amber-100 text-amber-700',
  business: 'bg-sky-100 text-sky-700',
  individual: 'bg-slate-100 text-slate-600',
};

const StatCard = ({ label, value, icon: Icon, tone = 'navy', testId }) => {
  const tones = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    navy: 'bg-[#002249]/5 border-[#002249]/20 text-[#002249]',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-600',
  };
  return (
    <div className={`u-card p-4 rounded-xl border ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className="mt-2 text-2xl font-bold leading-none">{value}</p>
    </div>
  );
};

const OnlineDot = ({ online }) => (
  <span className="inline-flex items-center gap-1.5">
    <span
      className={`inline-block w-2 h-2 rounded-full ${online ? 'bg-emerald-500 u-pulse-emerald' : 'bg-slate-300'}`}
    />
    <span className={`text-[11px] font-semibold uppercase tracking-wide ${online ? 'text-emerald-700' : 'text-slate-400'}`}>
      {online ? 'Online' : 'Offline'}
    </span>
  </span>
);

const AdminPresencePage = () => {
  const [data, setData] = useState({ users: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [onlineOnly, setOnlineOnly] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/presence`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load presence data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 60 * 1000); // auto-refresh every 60s
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    return (data.users || []).filter((u) => {
      if (onlineOnly && !u.online) return false;
      if (typeFilter !== 'all' && u.type !== typeFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          (u.name || '').toLowerCase().includes(s) ||
          (u.email || '').toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [data, search, typeFilter, onlineOnly]);

  const columns = [
    {
      id: 'name',
      label: 'User',
      accessor: (u) => u.name || '',
      render: (u) => (
        <div className="flex items-center gap-2.5 min-w-0">
          {u.picture ? (
            <Img variant="avatar" src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#002249] text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
              {(u.name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 truncate">{u.name || '—'}</p>
            <p className="text-[11.5px] text-slate-500 truncate">{u.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      accessor: (u) => u.type || 'member',
      render: (u) => (
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wide ${TYPE_COLOR[u.type] || 'bg-slate-100 text-slate-600'}`}>
          {u.type || 'member'}
        </span>
      ),
    },
    {
      id: 'online',
      label: 'Status',
      accessor: (u) => (u.online ? 1 : 0),
      render: (u) => <OnlineDot online={u.online} />,
    },
    {
      id: 'last_seen_at',
      label: 'Last Seen',
      accessor: (u) => u.last_seen_at || '',
      render: (u) => (
        <span className="text-[12px] text-slate-600">{formatRelative(u.last_seen_at)}</span>
      ),
    },
    {
      id: 'last_login_at',
      label: 'Last Login',
      accessor: (u) => u.last_login_at || '',
      render: (u) => (
        <span className="text-[12px] text-slate-600">{formatRelative(u.last_login_at)}</span>
      ),
    },
    {
      id: 'access',
      label: 'Activity Access',
      accessor: (u) => (u.activity_visibility_granted ? 1 : 0),
      render: (u) => (
        u.activity_visibility_granted ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            <Shield className="w-3 h-3" /> Granted
          </span>
        ) : (
          <span className="text-[11px] text-slate-400">—</span>
        )
      ),
    },
  ];

  const stats = data.stats || { online_now: 0, active_today: 0, active_7d: 0, inactive: 0, total: 0 };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-presence-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#002249] flex items-center gap-2">
            <Activity className="w-6 h-6" /> User Presence
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Live activity monitor for every account on the platform. Online = active within 5 minutes.
          </p>
        </div>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 text-[13px] font-medium text-[#002249] bg-white border border-slate-200 hover:bg-slate-50 px-3.5 py-2 rounded-lg shadow-sm"
          data-testid="presence-refresh-btn"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Online Now" value={stats.online_now} icon={Circle} tone="emerald" testId="stat-online-now" />
        <StatCard label="Active Today" value={stats.active_today} icon={UserCheck} tone="navy" testId="stat-active-today" />
        <StatCard label="Active 7d" value={stats.active_7d} icon={Clock} tone="navy" testId="stat-active-7d" />
        <StatCard label="Inactive" value={stats.inactive} icon={UserX} tone="slate" testId="stat-inactive" />
        <StatCard label="Total" value={stats.total} icon={Users} tone="navy" testId="stat-total" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-slate-200 p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            className="w-full pl-9 pr-3 h-10 text-[13px] rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
            data-testid="presence-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {['all', 'admin', 'leadership', 'business', 'individual'].map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 h-9 rounded-full text-[12px] font-semibold capitalize border transition-all ${
                typeFilter === t
                  ? 'bg-[#002249] text-white border-[#002249]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-[#002249]/40'
              }`}
              data-testid={`presence-type-${t}`}
            >
              {t}
            </button>
          ))}
          <label className="inline-flex items-center gap-2 ml-1 text-[12px] font-semibold text-slate-700 cursor-pointer select-none" data-testid="presence-online-only">
            <input
              type="checkbox"
              checked={onlineOnly}
              onChange={(e) => setOnlineOnly(e.target.checked)}
              className="w-4 h-4 accent-emerald-500"
            />
            Online only
          </label>
        </div>
      </motion.div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <SortableTable
          columns={columns}
          data={filtered}
          defaultSort={{ id: 'online', direction: 'desc' }}
          rowTestId={(u, i) => `presence-row-${i}`}
          emptyMessage={loading ? 'Loading activity…' : 'No users match your filters.'}
          stickyHeader
        />
      </div>

      <p className="mt-4 text-[11.5px] text-slate-400 flex items-center gap-1.5">
        <Eye className="w-3 h-3" /> Auto-refreshing every 60 seconds. Heartbeat window = 5 minutes.
      </p>
    </div>
  );
};

export default AdminPresencePage;
